import { SkuBackfillItem, SkuBackfillOutcome, SkuBackfillResult } from '@/console/api/types'
import { PastedLines, Tone, parsePastedLines, parsePositiveId } from './skuLookup'

/** sku_id 백필 패널의 순수 로직. 입력 검증 · 판정 묶음 · 재시도 판정은 여기 두고 패널은 결과만 그린다. */

export const SKU_BACKFILL_MAX_ITEMS = 100
/** `standard_order_product.sku_id` 가 varchar(14) 다. */
export const SKU_ID_MAX_LENGTH = 14
/** standard_order_product.id 보다 훨씬 긴 숫자는 주문번호를 잘못 붙여넣은 것일 가능성이 크다. */
const ORDER_NUMBER_LIKE_MIN_DIGITS = 12

export type SkuBackfillTone = 'success' | 'retryable' | 'invalid' | 'neutral'

/** 판정 8종을 네 묶음(바뀐 것 · 다시 보내면 되는 것 · 입력을 고쳐야 하는 것 · 할 일 없음)으로 나눈다. */
export const SKU_BACKFILL_OUTCOME_TONE = {
  UPDATED: 'success',
  UNCHANGED: 'neutral',
  SKIPPED_ALREADY_FILLED: 'neutral',
  NOT_FOUND: 'invalid',
  INVALID_SKU: 'invalid',
  SKU_LOOKUP_FAILED: 'retryable',
  CONFLICT: 'retryable',
  WRITE_FAILED: 'retryable',
} as const satisfies Record<SkuBackfillOutcome, SkuBackfillTone>

export const SKU_BACKFILL_TONE_TAG = {
  success: 'good',
  retryable: 'warn',
  invalid: 'crit',
  neutral: 'neutral',
} as const satisfies Record<SkuBackfillTone, Tone>

export const SKU_BACKFILL_OUTCOMES = Object.keys(SKU_BACKFILL_OUTCOME_TONE) as SkuBackfillOutcome[]

export const SKU_BACKFILL_RETRY = { resend: 'resend', recheck: 'recheck' } as const
export type SkuBackfillRetry = (typeof SKU_BACKFILL_RETRY)[keyof typeof SKU_BACKFILL_RETRY]

export const SKU_BACKFILL_RETRY_LABEL = {
  resend: '다시 보내기',
  recheck: '다시 조회한 뒤 판단',
} as const satisfies Record<SkuBackfillRetry, string>

/**
 * 재시도 버튼은 다시 보내면 결과가 달라질 수 있는 세 판정에만 둔다. CONFLICT 는 읽은 뒤 다른 경로가
 * 값을 바꾼 것이라 그대로 다시 보내지 않고 현재 값을 먼저 확인한다.
 */
export const SKU_BACKFILL_RETRY_BY_OUTCOME = {
  UPDATED: null,
  UNCHANGED: null,
  SKIPPED_ALREADY_FILLED: null,
  NOT_FOUND: null,
  INVALID_SKU: null,
  SKU_LOOKUP_FAILED: SKU_BACKFILL_RETRY.resend,
  CONFLICT: SKU_BACKFILL_RETRY.recheck,
  WRITE_FAILED: SKU_BACKFILL_RETRY.resend,
} as const satisfies Record<SkuBackfillOutcome, SkuBackfillRetry | null>

const NULL_SKU_ID_LABEL = 'NULL'

/** 결과 행의 값 칸. UPDATED 는 되돌릴 근거라 `이전 → 요청` 을 접지 않고 그대로 보인다. */
export const SKU_BACKFILL_VALUE_DESCRIPTION = {
  UPDATED: (result) => `${result.previousSkuId ?? NULL_SKU_ID_LABEL} → ${result.requestedSkuId}`,
  UNCHANGED: (result) => `현재 ${result.requestedSkuId} (요청과 같음)`,
  SKIPPED_ALREADY_FILLED: (result) => `현재 ${result.previousSkuId ?? '?'} · 요청 ${result.requestedSkuId}`,
  NOT_FOUND: (result) => `요청 ${result.requestedSkuId}`,
  INVALID_SKU: (result) => `요청 ${result.requestedSkuId}`,
  SKU_LOOKUP_FAILED: (result) => `요청 ${result.requestedSkuId}`,
  CONFLICT: (result) => `요청 ${result.requestedSkuId}`,
  WRITE_FAILED: (result) => `요청 ${result.requestedSkuId}`,
} as const satisfies Record<SkuBackfillOutcome, (result: SkuBackfillResult) => string>

/** 응답이 알려 준 행의 현재 값. undefined 는 이 판정으로는 현재 값을 알 수 없다는 뜻이다. */
const CURRENT_SKU_ID_BY_OUTCOME = {
  UPDATED: (result) => result.requestedSkuId,
  UNCHANGED: (result) => result.requestedSkuId,
  SKIPPED_ALREADY_FILLED: (result) => result.previousSkuId ?? undefined,
  NOT_FOUND: () => undefined,
  INVALID_SKU: () => undefined,
  SKU_LOOKUP_FAILED: () => undefined,
  CONFLICT: () => undefined,
  WRITE_FAILED: () => undefined,
} as const satisfies Record<SkuBackfillOutcome, (result: SkuBackfillResult) => string | undefined>

/** 저장소의 백필 실행 기록 중 현재 값 판정에 쓰는 부분. */
type SubmittedResults = { submittedAt: string; results: SkuBackfillResult[] }

/** 응답이 알려 준 현재 값과, 그 값을 돌려받은 제출 시각. 그 뒤에 값이 바뀌었을 수 있다. */
export type KnownCurrentSkuId = { skuId: string; submittedAt: string }

const findKnownSkuIdInRun = (run: SubmittedResults, standardOrderProductId: number): string | undefined =>
  run.results
    .filter((result) => result.standardOrderProductId === standardOrderProductId)
    .map((result) => CURRENT_SKU_ID_BY_OUTCOME[result.outcome](result))
    .find((skuId) => skuId !== undefined)

/** 덮어쓰기 확인창에 싣는 현재 값 — 최근 제출부터 훑어 처음 알려진 값을 쓴다. */
export const findKnownCurrentSkuId = (runsNewestFirst: SubmittedResults[], standardOrderProductId: number): KnownCurrentSkuId | undefined =>
  runsNewestFirst
    .map((run) => ({ skuId: findKnownSkuIdInRun(run, standardOrderProductId), submittedAt: run.submittedAt }))
    .find((known): known is KnownCurrentSkuId => known.skuId !== undefined)

export type ParsedBackfillInput = PastedLines<SkuBackfillItem> & {
  /** 같은 id 에 서로 다른 skuId 가 붙은 id. 어느 쪽이 맞는지 화면도 서버도 고를 수 없다. */
  conflictingIds: number[]
  orderNumberLikeIds: number[]
}

const parseBackfillTokens = (tokens: string[]): SkuBackfillItem | null => {
  if (tokens.length !== 2) return null
  const [idToken, skuId] = tokens
  const standardOrderProductId = parsePositiveId(idToken)
  if (standardOrderProductId === null || skuId.length > SKU_ID_MAX_LENGTH) return null
  return { standardOrderProductId, skuId }
}

// 완전히 같은 줄은 이미 합쳐졌으니 id 가 다시 나오면 skuId 가 다르다는 뜻이다.
const findConflictingIds = (items: SkuBackfillItem[]): number[] => {
  const seenIds = new Set<number>()
  const conflictingIds = new Set<number>()
  items.forEach(({ standardOrderProductId }) => {
    if (seenIds.has(standardOrderProductId)) conflictingIds.add(standardOrderProductId)
    seenIds.add(standardOrderProductId)
  })
  return [...conflictingIds]
}

/** 한 줄에 `standardOrderProductId skuId`. 완전히 같은 줄은 합치고, 같은 id · 다른 skuId 는 따로 모은다. */
export const parseBackfillInput = (raw: string): ParsedBackfillInput => {
  const parsed = parsePastedLines(raw, parseBackfillTokens, (item) => `${item.standardOrderProductId}\n${item.skuId}`)
  return {
    ...parsed,
    conflictingIds: findConflictingIds(parsed.items),
    orderNumberLikeIds: parsed.items
      .map((item) => item.standardOrderProductId)
      .filter((id) => String(id).length >= ORDER_NUMBER_LIKE_MIN_DIGITS),
  }
}

export const exceedsBackfillLimit = (parsed: ParsedBackfillInput): boolean => parsed.items.length > SKU_BACKFILL_MAX_ITEMS

/** 유효하지 않은 값이 하나라도 섞이면 막는다 — 유효한 것만 보내면 담당자는 전부 채워졌다고 믿는다. */
export const isSubmittableBackfill = (parsed: ParsedBackfillInput): boolean =>
  parsed.items.length > 0 && parsed.invalidLines.length === 0 && parsed.conflictingIds.length === 0 && !exceedsBackfillLimit(parsed)

/** 재시도 버튼이 입력란에 다시 담을 때 쓴다 — `parseBackfillInput` 이 그대로 읽는 모양이다. */
export const toBackfillInputText = (items: SkuBackfillItem[]): string =>
  items.map((item) => `${item.standardOrderProductId} ${item.skuId}`).join('\n')

export const toRetryItem = (result: SkuBackfillResult): SkuBackfillItem => ({
  standardOrderProductId: result.standardOrderProductId,
  skuId: result.requestedSkuId,
})
